// ============================================================================
// NOTICE TYPE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { noticeTypeEndpoints, NOTICE_TYPE_BASE_PATH } from '../endpoints/noticeType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  NoticeType,
  NoticeTypeListItem,
  CreateNoticeTypeRequest,
  UpdateNoticeTypeRequest,
  NoticeTypeSearchRequest,
} from '../types/noticeType.types';

class NoticeTypeService {
  async getAll(params?: NoticeTypeSearchRequest): Promise<PaginatedResponse<NoticeTypeListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(NOTICE_TYPE_BASE_PATH, params)
      : noticeTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<NoticeType>> {
    const response = await get<ApiResponse<NoticeType>>(noticeTypeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<NoticeType>> {
    const response = await get<ApiResponse<NoticeType>>(noticeTypeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateNoticeTypeRequest): Promise<ApiResponse<NoticeType>> {
    const response = await post<ApiResponse<NoticeType>>(noticeTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateNoticeTypeRequest): Promise<ApiResponse<NoticeType>> {
    const response = await put<ApiResponse<NoticeType>>(noticeTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(noticeTypeEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${noticeTypeEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : noticeTypeEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const noticeTypeService = new NoticeTypeService();
export default noticeTypeService;
